import React from 'react';

function renderInline(text = '', keyPrefix = 'inline') {
  const value = String(text || '');
  const parts = value.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return (
        <strong key={`${keyPrefix}-${index}`} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }

    return <React.Fragment key={`${keyPrefix}-${index}`}>{part}</React.Fragment>;
  });
}

function isTableLine(line = '') {
  return /^\s*\|.+\|\s*$/.test(line);
}

function isBulletLine(line = '') {
  return /^\s*-\s+/.test(line);
}

function isHeadingLine(line = '') {
  return /^\s{0,3}#{1,6}\s+/.test(line);
}

function parseTable(lines = []) {
  const rows = lines.map((line) =>
    line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((cell) => cell.trim())
  );

  if (rows.length < 2) {
    return null;
  }

  const header = rows[0];
  const separator = rows[1];
  const isSeparatorRow = separator.every((cell) => /^:?-{3,}:?$/.test(cell));

  if (!isSeparatorRow) {
    return null;
  }

  return {
    header,
    rows: rows.slice(2),
  };
}

function isSourceInfoLine(text = '') {
  return /^(?:\*?\s*\s*)?sumber informasi\s*:/i.test(String(text || '').trim());
}

function parseBlocks(text = '') {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (isHeadingLine(line)) {
      const match = line.match(/^\s{0,3}(#{1,6})\s+(.*)$/);
      blocks.push({
        type: 'heading',
        level: match ? match[1].length : 2,
        text: match ? match[2].trim() : trimmed,
      });
      index += 1;
      continue;
    }

    if (isTableLine(line)) {
      const tableLines = [];
      while (index < lines.length && isTableLine(lines[index])) {
        tableLines.push(lines[index]);
        index += 1;
      }

      const table = parseTable(tableLines);
      if (table) {
        blocks.push({ type: 'table', ...table });
      } else {
        blocks.push({ type: 'paragraph', text: tableLines.join('\n') });
      }
      continue;
    }

    if (isBulletLine(line)) {
      const items = [];
      while (index < lines.length && isBulletLine(lines[index])) {
        items.push(lines[index].replace(/^\s*-\s+/, '').trim());
        index += 1;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    const paragraphLines = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      !isHeadingLine(lines[index]) &&
      !isBulletLine(lines[index]) &&
      !isTableLine(lines[index])
    ) {
      paragraphLines.push(lines[index].trim());
      index += 1;
    }

    blocks.push({
      type: 'paragraph',
      text: paragraphLines.join(' '),
    });
  }

  return blocks;
}

export function Response({ children, className = '' }) {
  const text = typeof children === 'string' ? children : String(children ?? '');
  const blocks = parseBlocks(text);

  return (
    <div className={`space-y-4 text-[15px] leading-7 ${className}`.trim()}>
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          const sizeClass = block.level <= 2 ? 'text-base font-semibold' : 'text-sm font-semibold';
          return (
            <h3 key={`heading-${index}`} className={sizeClass}>
              {renderInline(block.text, `heading-${index}`)}
            </h3>
          );
        }

        if (block.type === 'list') {
          return (
            <ul key={`list-${index}`} className="list-disc space-y-2 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={`list-${index}-${itemIndex}`}>{renderInline(item, `list-${index}-${itemIndex}`)}</li>
              ))}
            </ul>
          );
        }

        if (block.type === 'table') {
          return (
            <div
              key={`table-${index}`}
              className="overflow-x-auto rounded-2xl border border-black/10 bg-white/70 shadow-sm"
            >
              <table className="min-w-full border-collapse text-sm leading-6">
                <thead className="bg-black/[0.04] text-left">
                  <tr>
                    {block.header.map((cell, cellIndex) => (
                      <th key={`table-head-${index}-${cellIndex}`} className="border-b border-black/10 px-4 py-3 font-semibold">
                        {renderInline(cell, `table-head-${index}-${cellIndex}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={`table-row-${index}-${rowIndex}`} className={rowIndex % 2 === 0 ? 'bg-white/80' : 'bg-black/[0.02]'}>
                      {row.map((cell, cellIndex) => (
                        <td key={`table-cell-${index}-${rowIndex}-${cellIndex}`} className="align-top border-b border-black/5 px-4 py-3">
                          {renderInline(cell, `table-cell-${index}-${rowIndex}-${cellIndex}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        const isSourceInfo = isSourceInfoLine(block.text);
        return (
          <p
            key={`paragraph-${index}`}
            className={`whitespace-pre-wrap ${isSourceInfo ? 'text-[11px] leading-5 text-[#8e8ea0]' : ''}`.trim()}
          >
            {renderInline(block.text, `paragraph-${index}`)}
          </p>
        );
      })}
    </div>
  );
}
